import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDutyRoster } from './DutyRosterContext';
import { InitialsAvatar } from './shared';
import { localDateKey, todayKey } from '../../../utils/datetime';
import { useAuth } from '../../../context/AuthContext';
import { useLiveRefresh } from '../../../context/LiveUpdatesContext';
import { homeAgencyService } from '../../../services/agencyService';
import AttendanceModal from '../agencies/AttendanceModal';
import { BOOKING_STATE, clockOf, localInputOf } from '../../agency/shared';

/**
 * The rota's Agency group — confirmed agency bookings for the week (or day)
 * on screen, one row per agency worker. A display group only: agency staff
 * are never put into the home's teams or recurring patterns. Each cell shows
 * the booked times and what was clocked; clicking it opens the same
 * attendance modal as Staff > Agencies (approve, edit times, did not attend).
 *
 * Day view draws the same 24-hour lane as the home's own teams: the booked
 * times as a Scheduled bar, what was clocked as an Actual bar beneath it.
 *
 * New agency cover is requested from Staff > Agencies > Cover requests.
 */

/** Minutes from midnight of `ymd` (London) — 'HH:MM' or an ISO instant. */
const minutesOn = (ymd, value) => {
  if (!value) return null;
  const s = String(value);
  if (/^\d{2}:\d{2}/.test(s)) return Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
  const local = localInputOf(s); // 'YYYY-MM-DDTHH:MM', London
  if (!local) return null;
  const dayDiff = Math.round((Date.parse(`${local.slice(0, 10)}T00:00:00Z`) - Date.parse(`${ymd}T00:00:00Z`)) / 86400000);
  return dayDiff * 1440 + Number(local.slice(11, 13)) * 60 + Number(local.slice(14, 16));
};

// Bar colours per state — the team rows' palette (DutyRoster renderDayRow).
const SCHED_TONE = {
  missed: 'bg-rose-50 border-rose-300 text-rose-900 dark:bg-rose-500/10 dark:border-rose-500/40 dark:text-rose-100',
  no_show: 'bg-rose-50 border-rose-300 text-rose-900 dark:bg-rose-500/10 dark:border-rose-500/40 dark:text-rose-100',
  due: 'bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-500/10 dark:border-amber-500/40 dark:text-amber-100',
};
const SCHED_DEFAULT = 'bg-blue-50 border-blue-400 text-blue-900 dark:bg-blue-500/10 dark:border-blue-500/40 dark:text-blue-100';
const SCHED_BADGE = {
  missed: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200',
  no_show: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200',
  due: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200',
  in_progress: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-200',
};
const SCHED_BADGE_DEFAULT = 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200';
const ACTUAL_TONE = {
  live: ['bg-cyan-50 border-cyan-300 text-cyan-900 dark:bg-cyan-500/10 dark:border-cyan-500/40 dark:text-cyan-100', 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-200'],
  to_approve: ['bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-500/10 dark:border-amber-500/40 dark:text-amber-100', 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200'],
  approved: ['bg-emerald-50 border-emerald-300 text-emerald-900 dark:bg-emerald-500/10 dark:border-emerald-500/40 dark:text-emerald-100', 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200'],
};

const CELL_TONE = {
  upcoming: 'border-l-[#2563EB] bg-[#EEF3FF] dark:bg-blue-900/20',
  due: 'border-l-amber-500 bg-amber-50/70 dark:bg-amber-500/10',
  in_progress: 'border-l-blue-500 bg-blue-50/70 dark:bg-blue-500/10',
  completed: 'border-l-emerald-600 bg-emerald-50/60 dark:bg-emerald-500/10',
  missed: 'border-l-rose-500 bg-rose-50/70 dark:bg-rose-500/10',
  no_show: 'border-l-rose-400 bg-rose-50/50 dark:bg-rose-500/10',
};

const AgencyRotaGroup = () => {
  const { weekDates, rosterView, dailyDate, EMP_COL, HOUR_PX, BOARD_W, hourLabels, laneBox, shortDays } = useDutyRoster();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [open, setOpen] = useState(null);

  const days = useMemo(
    () => (rosterView === 'weekly' ? weekDates : [dailyDate]).filter(Boolean).map(localDateKey),
    [rosterView, weekDates, dailyDate],
  );
  const from = days[0];
  const to = days[days.length - 1];
  const isDomiciliary = (user?.organization?.organization_type || '').toLowerCase() === 'domiciliary';

  const load = useCallback(async () => {
    if (!from || isDomiciliary) return;
    try {
      const res = await homeAgencyService.rota(from, to);
      setBookings(res.data || []);
      setHidden(false);
    } catch (err) {
      // No Agencies access (or not a care home): the group simply isn't drawn.
      if ([403, 404].includes(err?.response?.status)) setHidden(true);
    }
  }, [from, to, isDomiciliary]);
  useEffect(() => { load(); }, [load]);
  // Confirmations, cancellations and an agency carer clocking in show here at
  // once. load() has no spinner, so the lanes and scroll position stay put.
  useLiveRefresh(['agency'], () => load());

  if (hidden || isDomiciliary || !from) return null;

  // Rows: one per worker, cells by START date.
  const workers = [];
  const byWorker = new Map();
  bookings.forEach((b) => {
    const key = b.worker?.id;
    if (!byWorker.has(key)) { byWorker.set(key, []); workers.push(b); }
    byWorker.get(key).push(b);
  });
  const attention = bookings.filter((b) => b.state === 'missed' || b.hours === 'to_approve').length;

  const cell = (b) => {
    const meta = BOOKING_STATE[b.state] || { label: b.state };
    const a = b.actual;
    return (
      <button
        key={b.id}
        type="button"
        onClick={() => setOpen(b)}
        className={`w-full text-left rounded-lg border border-[#E2E8F0] dark:border-gray-800 border-l-[3px] px-2 py-1.5 hover:shadow-sm transition ${CELL_TONE[b.state] || 'bg-white dark:bg-[#262b33]'}`}
      >
        <p className="text-[11px] font-extrabold text-[#1E293B] dark:text-gray-100">{b.start_time}–{b.end_time}</p>
        {a?.clock_in && (
          <p className="text-[10px] text-[#475569] dark:text-gray-300">Actual {clockOf(a.clock_in)}–{a.clock_out ? clockOf(a.clock_out) : '…'}</p>
        )}
        <p className="text-[9.5px] font-bold uppercase tracking-wide text-[#64748B] dark:text-gray-400 truncate">
          {b.hours === 'approved' ? 'Approved' : b.hours === 'to_approve' ? 'Hours to approve' : meta.label}
        </p>
        {b.unit && <p className="text-[9.5px] text-[#94A3B8] truncate">{b.unit}</p>}
      </button>
    );
  };

  // Day view: one 24-hour lane per worker, Scheduled on top and Actual below,
  // drawn exactly like the team rows above it.
  const dayRow = (w) => {
    const day = days[0];
    const bars = [];
    (byWorker.get(w.worker?.id) || []).filter((b) => b.shift_date === day).forEach((b) => {
      const meta = BOOKING_STATE[b.state] || { label: b.state };
      const startMin = minutesOn(day, b.start_time);
      let endMin = minutesOn(day, b.end_time);
      const overnight = (b.end_date && b.end_date !== b.shift_date) || endMin <= startMin;
      if (endMin <= startMin) endMin += 1440;
      const sched = laneBox(startMin, endMin);
      bars.push(
        <div
          key={`s-${b.id}`}
          onClick={() => setOpen(b)}
          className={`absolute top-[9px] h-[57px] rounded-[11px] border-2 px-3 py-2 overflow-hidden cursor-pointer hover:shadow-md transition ${SCHED_TONE[b.state] || SCHED_DEFAULT}`}
          style={{ left: sched.left, width: sched.width }}
        >
          <div className="flex items-center justify-between gap-1.5">
            <strong className="text-[12px] truncate">Scheduled{b.unit ? ` · ${b.unit}` : ''}</strong>
            <span className={`shrink-0 px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase tracking-wide ${SCHED_BADGE[b.state] || SCHED_BADGE_DEFAULT}`}>{meta.label}</span>
          </div>
          <div className="text-[10px] font-bold mt-1 whitespace-nowrap">◷ {b.start_time} – {b.end_time}{overnight ? ' (+1)' : ''}</div>
        </div>,
      );

      const a = b.actual;
      if (!a?.clock_in) return;
      const asMin = minutesOn(day, a.clock_in);
      let aeMin = a.clock_out ? minutesOn(day, a.clock_out) : minutesOn(day, new Date().toISOString());
      if (aeMin <= asMin) aeMin = asMin + 1;
      if (aeMin <= 0 || asMin >= 1440) return; // clocked wholly outside this day
      const act = laneBox(asMin, aeMin);
      const kind = b.state === 'in_progress' ? 'live' : b.hours === 'approved' ? 'approved' : 'to_approve';
      const [tone, badge] = ACTUAL_TONE[kind];
      const label = kind === 'live' ? 'On shift' : kind === 'approved' ? 'Approved' : 'To approve';
      bars.push(
        <div
          key={`a-${b.id}`}
          onClick={() => setOpen(b)}
          className={`absolute top-[74px] h-[58px] rounded-[11px] border-2 px-3 py-1.5 overflow-hidden cursor-pointer hover:shadow-md transition ${tone}`}
          style={{ left: act.left, width: act.width }}
        >
          <div className="flex items-center justify-between gap-1.5">
            <strong className="text-[10px] truncate">Actual</strong>
            <span className={`shrink-0 px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase tracking-wide ${badge}`}>{label}</span>
          </div>
          <div className="text-[10px] font-bold mt-0.5 whitespace-nowrap">◷ {clockOf(a.clock_in)} – {a.clock_out ? clockOf(a.clock_out) : '…'}</div>
        </div>,
      );
    });
    return (
      <div
        key={w.worker?.id}
        className="relative grid border-t border-[#EDF1F6] dark:border-gray-800"
        style={{ gridTemplateColumns: `${EMP_COL}px repeat(24, ${HOUR_PX}px)`, height: 142 }}
      >
        <div className="sticky left-0 z-[11] bg-white dark:bg-[#262b33] flex flex-col items-center justify-center gap-1.5 px-2 shadow-[5px_0_10px_rgba(52,70,98,0.07)]">
          <InitialsAvatar name={w.worker?.name} size="md" />
          <span className="text-[11px] font-semibold text-[#1E293B] dark:text-gray-200 leading-tight text-center line-clamp-2">{w.worker?.name}</span>
          <span className="text-[9.5px] text-[#94A3B8] text-center leading-tight">{w.agency?.name}</span>
        </div>
        {hourLabels.map((_, i) => <div key={i} className="border-r border-[#EDF1F6] dark:border-gray-800" />)}
        {bars}
      </div>
    );
  };

  const dayTimeline = () => {
    const now = new Date();
    const isViewingToday = days[0] === todayKey();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const markerLeft = EMP_COL + (nowMin / 60) * HOUR_PX;
    const nowLabel = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    return (
      <div className="overflow-x-auto">
        <div className="relative bg-white dark:bg-[#262b33]" style={{ minWidth: BOARD_W }}>
          <div className="grid bg-[#FBFCFE] dark:bg-[#20242c] border-b border-[#EDF1F6] dark:border-gray-800" style={{ gridTemplateColumns: `${EMP_COL}px repeat(24, ${HOUR_PX}px)` }}>
            <div className="sticky left-0 z-[13] h-[45px] flex items-center justify-center bg-[#FBFCFE] dark:bg-[#20242c] text-[#5e6e85] dark:text-gray-400 text-[10px] font-bold tracking-[0.1em] shadow-[5px_0_10px_rgba(52,70,98,0.07)]">AGENCY STAFF</div>
            {hourLabels.map((h) => (
              <div key={h} className="h-[45px] px-2 py-3 border-r border-[#EDF1F6] dark:border-gray-800 text-[#91a1b6] dark:text-gray-500 text-[10px] font-bold">{h}:00</div>
            ))}
          </div>
          {isViewingToday && markerLeft >= EMP_COL && markerLeft <= BOARD_W && (
            <div className="absolute z-[2] w-px bg-cyan-600/50 dark:bg-cyan-400/50 pointer-events-none" style={{ left: markerLeft, top: 45, bottom: 0 }}>
              <div className="absolute -top-[1px] -left-[5px] w-2.5 h-2.5 rounded-full bg-cyan-600 dark:bg-cyan-400 border-2 border-white dark:border-[#262b33]" />
              <div className="absolute -top-[18px] -left-3 px-1 rounded border border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-500/40 dark:bg-cyan-500/20 dark:text-cyan-200 text-[7px] font-extrabold leading-[14px] h-[14px]">{nowLabel}</div>
            </div>
          )}
          {workers.map(dayRow)}
        </div>
      </div>
    );
  };

  return (
    <section className="bg-white dark:bg-[#262b33] rounded-2xl border border-[#E2E8F0] dark:border-gray-800 shadow-sm overflow-hidden">
      <div className="min-h-[52px] flex items-center justify-between gap-4 px-4 py-2.5 border-b border-[#E2E8F0] dark:border-gray-800">
        <div className="flex items-center gap-2.5 min-w-0 flex-wrap">
          <button onClick={() => setCollapsed((c) => !c)} className="w-7 h-7 rounded-md border border-[#E2E8F0] dark:border-gray-800 text-[#65758c] dark:text-gray-400 grid place-items-center text-[14px] leading-none">{collapsed ? '›' : '⌄'}</button>
          <span className="w-2.5 h-2.5 rounded-sm shrink-0 bg-amber-500" />
          <span className="text-[12px] font-extrabold tracking-[0.12em] text-[#1E293B] dark:text-gray-200 uppercase">Agency</span>
          <span className="px-1.5 py-1 rounded-md bg-[#F2F6FC] dark:bg-[#31373f] text-[#66768c] dark:text-gray-400 text-[8px] font-extrabold">{workers.length} staff</span>
          <span className="px-1.5 py-1 rounded-md bg-[#F2F6FC] dark:bg-[#31373f] text-[#66768c] dark:text-gray-400 text-[8px] font-extrabold">{bookings.length} shifts</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => navigate('/manager/agencies?tab=requests')}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#D5DFEB] dark:border-gray-700 text-[#475569] dark:text-gray-400 text-[9px] font-extrabold whitespace-nowrap hover:bg-gray-50 dark:hover:bg-[#31373f] transition"
          >
            <span className="text-[12px] leading-none">+</span> Agency cover
          </button>
          {attention > 0 && (
            <button
              onClick={() => navigate('/manager/agencies?tab=hours')}
              className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/10 text-rose-600 dark:text-rose-400 text-[9px] font-extrabold whitespace-nowrap"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />Review <b className="px-1 rounded bg-rose-500 text-white">{attention}</b>
            </button>
          )}
        </div>
      </div>
      {!collapsed && (workers.length === 0 ? (
        <div className="h-[90px] flex items-center justify-center text-[12px] text-[#7d8da3] dark:text-gray-400">
          No agency shifts booked {rosterView === 'weekly' ? 'this week' : 'this day'}.
        </div>
      ) : rosterView !== 'weekly' ? dayTimeline() : (
        <div className="overflow-x-auto">
          <div className="min-w-[1166px]">
            <div className="grid" style={{ gridTemplateColumns: `${EMP_COL}px repeat(${days.length}, minmax(150px, 1fr))` }}>
              <div className="sticky left-0 z-[13] h-[46px] flex items-center justify-center bg-[#FBFCFE] dark:bg-[#20242c] border-r border-[#EDF1F6] dark:border-gray-800 text-[#62738a] dark:text-gray-400 text-[11px] font-bold tracking-[0.1em]">AGENCY STAFF</div>
              {days.map((d) => {
                const date = new Date(`${d}T12:00:00`);
                return (
                  <div key={d} className={`h-[46px] flex items-center justify-center border-r border-[#EDF1F6] dark:border-gray-800 text-[11px] font-bold ${d === todayKey() ? 'bg-[#F2F7FF] dark:bg-blue-500/10 text-blue-600 dark:text-blue-300' : 'bg-[#FBFCFE] dark:bg-[#20242c] text-[#62738a] dark:text-gray-400'}`}>
                    {shortDays[date.getDay()]} <b className="ml-1.5">{date.getDate()}</b>
                  </div>
                );
              })}
            </div>
            {workers.map((w) => (
              <div key={w.worker?.id} className="grid border-t border-[#EDF1F6] dark:border-gray-800 min-h-[96px]" style={{ gridTemplateColumns: `${EMP_COL}px repeat(${days.length}, minmax(150px, 1fr))` }}>
                <div className="sticky left-0 z-[11] bg-white dark:bg-[#262b33] flex flex-col items-center justify-center gap-1.5 px-2 border-r border-[#EDF1F6] dark:border-gray-800">
                  <InitialsAvatar name={w.worker?.name} size="md" />
                  <span className="text-[11px] font-semibold text-[#1E293B] dark:text-gray-200 leading-tight text-center">{w.worker?.name}</span>
                  <span className="text-[9.5px] text-[#94A3B8] text-center leading-tight">{w.agency?.name}</span>
                </div>
                {days.map((d) => (
                  <div key={d} className="border-r border-[#EDF1F6] dark:border-gray-800 p-1.5 flex flex-col gap-1.5 min-w-0">
                    {(byWorker.get(w.worker?.id) || []).filter((b) => b.shift_date === d).map(cell)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
      <AttendanceModal booking={open} onClose={() => setOpen(null)} onChanged={load} />
    </section>
  );
};

export default AgencyRotaGroup;
